import React, { useMemo, useState, useEffect, useCallback } from "react";

/**
 * CareLogix™ ORACLE — FX Risk Calculator + Paper Mode + Daily Loss Cap
 * Position Manager (Phase 26D).
 *
 * Additive only. No autonomous order execution. All numeric outputs are
 * research/decision-support helpers — the user must still confirm any
 * stage/place action through the existing OANDA FX Trading Screen flow.
 *
 * Persistence (localStorage):
 *   carelogix_fx_risk_v1 — JSON snapshot of risk settings + paper P/L log.
 */

const LS_KEY = "carelogix_fx_risk_v1";

const DEFAULTS = {
  accountBalance: 10000,
  riskPctPerTrade: 1, // % of account
  dailyLossCapPct: 3, // % of account
  paperMode: true,
  paperPositions: [], // [{id, side, instrument, units, entry, stop, target, status, opened_at, pnl_pips, pnl_usd}]
  paperPnlToday: 0,
  paperDayKey: null,
};

function loadState() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

function saveState(state) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function todayUtcKey() {
  return new Date().toISOString().slice(0, 10);
}

function fmtPct(v) {
  if (typeof v !== "number") return "—";
  return `${v.toFixed(2)}%`;
}

function fmtUSD(v) {
  if (typeof v !== "number") return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/**
 * Position sizing heuristic for FX. Pip value approximation:
 *   - Standard pip value for USD-quote pairs: $10 per pip per 100k units.
 *   - For non-USD quote pairs (e.g., EUR/GBP), this becomes an estimate only.
 * Risk per trade USD = accountBalance * (riskPctPerTrade / 100).
 * Suggested units = Risk USD / (stop_pips * pip value per unit).
 */
function suggestedUnits({ accountBalance, riskPctPerTrade, stopPips }) {
  const riskUsd = (accountBalance || 0) * ((riskPctPerTrade || 0) / 100);
  const stop = Math.max(1, Number(stopPips) || 0);
  // approximation: $1 per pip per 10k units for USD-quoted pairs
  const usdPerPipPerUnit = 0.0001; // 1 unit ⇒ 0.0001 USD per pip equiv (USD quote)
  const units = riskUsd / (stop * usdPerPipPerUnit);
  if (!Number.isFinite(units) || units <= 0) return 0;
  return Math.round(units);
}

export default function FxRiskCalculator({
  instrument,
  quote = {},
  decimals = 5,
  embedded = true,
}) {
  const [state, setState] = useState(loadState);

  // Roll the daily P/L counter if the UTC day has changed.
  useEffect(() => {
    const day = todayUtcKey();
    if (state.paperDayKey !== day) {
      const reset = { ...state, paperPnlToday: 0, paperDayKey: day };
      setState(reset);
      saveState(reset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const [side, setSide] = useState("buy");
  const [stopPips, setStopPips] = useState(20);
  const [targetPips, setTargetPips] = useState(40);

  const riskUsdPerTrade = useMemo(
    () => state.accountBalance * (state.riskPctPerTrade / 100),
    [state.accountBalance, state.riskPctPerTrade]
  );

  const dailyLossCapUsd = useMemo(
    () => state.accountBalance * (state.dailyLossCapPct / 100),
    [state.accountBalance, state.dailyLossCapPct]
  );

  const units = useMemo(
    () =>
      suggestedUnits({
        accountBalance: state.accountBalance,
        riskPctPerTrade: state.riskPctPerTrade,
        stopPips,
      }),
    [state.accountBalance, state.riskPctPerTrade, stopPips]
  );

  const rr = useMemo(() => {
    if (!stopPips || stopPips <= 0) return 0;
    return (targetPips || 0) / stopPips;
  }, [targetPips, stopPips]);

  const dailyCapHit = Math.abs(state.paperPnlToday) >= dailyLossCapUsd && state.paperPnlToday < 0;
  const dailyRemaining = Math.max(0, dailyLossCapUsd - Math.max(0, -state.paperPnlToday));

  const onChangeBalance = (e) =>
    setState((s) => ({ ...s, accountBalance: Math.max(0, parseFloat(e.target.value || "0")) }));
  const onChangeRiskPct = (e) =>
    setState((s) => ({ ...s, riskPctPerTrade: Math.max(0, parseFloat(e.target.value || "0")) }));
  const onChangeCapPct = (e) =>
    setState((s) => ({ ...s, dailyLossCapPct: Math.max(0, parseFloat(e.target.value || "0")) }));
  const togglePaper = () => setState((s) => ({ ...s, paperMode: !s.paperMode }));

  const addPaperPosition = useCallback(() => {
    if (dailyCapHit) return;
    const entryPrice =
      side === "buy"
        ? typeof quote?.ask === "number"
          ? quote.ask
          : 1
        : typeof quote?.bid === "number"
        ? quote.bid
        : 1;
    const pip = decimals >= 4 ? 0.0001 : 0.01;
    const stop = side === "buy" ? entryPrice - stopPips * pip : entryPrice + stopPips * pip;
    const target = side === "buy" ? entryPrice + targetPips * pip : entryPrice - targetPips * pip;
    const id = `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const next = {
      id,
      side,
      instrument: instrument || "—",
      units,
      entry: entryPrice,
      stop,
      target,
      status: "OPEN",
      opened_at: new Date().toISOString(),
      pnl_pips: 0,
      pnl_usd: 0,
    };
    setState((s) => ({ ...s, paperPositions: [next, ...(s.paperPositions || [])].slice(0, 25) }));
  }, [dailyCapHit, side, quote, decimals, stopPips, targetPips, instrument, units]);

  const closePaperPosition = useCallback((id, outcome = "TARGET") => {
    setState((s) => {
      const list = (s.paperPositions || []).map((p) => {
        if (p.id !== id) return p;
        const pip = Math.abs(p.target - p.entry) > 0 ? Math.abs(p.target - p.entry) / (targetPips || 40) : 0.0001;
        let pnlPips = 0;
        let pnlUsd = 0;
        if (outcome === "TARGET") {
          pnlPips = targetPips;
          pnlUsd = (p.units || 0) * (pnlPips * pip);
        } else if (outcome === "STOP") {
          pnlPips = -stopPips;
          pnlUsd = (p.units || 0) * (pnlPips * pip);
        } else {
          pnlPips = 0;
          pnlUsd = 0;
        }
        return { ...p, status: outcome === "TARGET" ? "TP_HIT" : outcome === "STOP" ? "SL_HIT" : "CLOSED", pnl_pips: pnlPips, pnl_usd: Math.round(pnlUsd * 100) / 100 };
      });
      const updated = list.find((p) => p.id === id);
      const newDailyPnl = (s.paperPnlToday || 0) + (updated?.pnl_usd || 0);
      return { ...s, paperPositions: list, paperPnlToday: Math.round(newDailyPnl * 100) / 100 };
    });
  }, [stopPips, targetPips]);

  const resetDailyPnl = useCallback(() => {
    setState((s) => ({ ...s, paperPnlToday: 0, paperPositions: [] }));
  }, []);

  return (
    <section
      className={`fx-risk ${embedded ? "is-embedded" : ""}`}
      data-testid="fx-risk-calculator"
      style={S.wrap}
    >
      <header style={S.head}>
        <strong style={S.title}>Risk Calculator · Position Sizing</strong>
        <div style={S.headRight}>
          <span
            data-testid="fx-paper-pill"
            style={{
              ...S.modePill,
              color: state.paperMode ? "#10b981" : "#c5a059",
              borderColor: state.paperMode ? "#10b981" : "#c5a059",
              background: state.paperMode
                ? "rgba(16,185,129,0.12)"
                : "rgba(197,160,89,0.10)",
            }}
          >
            {state.paperMode ? "PAPER MODE · VALIDATION" : "LIVE-READY · MANUAL CONFIRM"}
          </span>
          <button
            type="button"
            onClick={togglePaper}
            style={S.btnSm}
            data-testid="fx-paper-toggle"
            aria-label="Toggle paper validation mode"
          >
            {state.paperMode ? "Disable Paper" : "Enable Paper"}
          </button>
        </div>
      </header>

      <div style={S.grid}>
        <label style={S.lbl}>
          <span>Account balance (USD)</span>
          <input
            type="number"
            min="0"
            step="100"
            value={state.accountBalance}
            onChange={onChangeBalance}
            data-testid="fx-risk-balance"
            style={S.input}
          />
        </label>
        <label style={S.lbl}>
          <span>Risk per trade %</span>
          <input
            type="number"
            min="0"
            max="5"
            step="0.1"
            value={state.riskPctPerTrade}
            onChange={onChangeRiskPct}
            data-testid="fx-risk-pct"
            style={S.input}
          />
        </label>
        <label style={S.lbl}>
          <span>Daily loss cap %</span>
          <input
            type="number"
            min="0"
            max="20"
            step="0.1"
            value={state.dailyLossCapPct}
            onChange={onChangeCapPct}
            data-testid="fx-daily-cap-pct"
            style={S.input}
          />
        </label>
        <label style={S.lbl}>
          <span>Stop (pips)</span>
          <input
            type="number"
            min="1"
            step="1"
            value={stopPips}
            onChange={(e) => setStopPips(parseInt(e.target.value || "0", 10))}
            data-testid="fx-risk-stop"
            style={S.input}
          />
        </label>
        <label style={S.lbl}>
          <span>Target (pips)</span>
          <input
            type="number"
            min="1"
            step="1"
            value={targetPips}
            onChange={(e) => setTargetPips(parseInt(e.target.value || "0", 10))}
            data-testid="fx-risk-target"
            style={S.input}
          />
        </label>
        <div style={S.lbl}>
          <span>Side</span>
          <div role="group" aria-label="Side" style={S.sideRow}>
            <button
              type="button"
              onClick={() => setSide("buy")}
              data-testid="fx-risk-side-buy"
              style={{ ...S.sideBtn, ...(side === "buy" ? S.sideBtnActiveBuy : {}) }}
            >BUY</button>
            <button
              type="button"
              onClick={() => setSide("sell")}
              data-testid="fx-risk-side-sell"
              style={{ ...S.sideBtn, ...(side === "sell" ? S.sideBtnActiveSell : {}) }}
            >SELL</button>
          </div>
        </div>
      </div>

      <div style={S.summary}>
        <div style={S.cell}>
          <span style={S.cellLbl}>SUGGESTED UNITS</span>
          <strong style={{ ...S.cellVal, color: "#c5a059" }} data-testid="fx-risk-units">{units.toLocaleString()}</strong>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>RISK / TRADE</span>
          <strong style={{ ...S.cellVal, color: "#fcd34d" }} data-testid="fx-risk-trade-usd">{fmtUSD(riskUsdPerTrade)}</strong>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>REWARD : RISK</span>
          <strong style={{ ...S.cellVal, color: rr >= 1.5 ? "#10b981" : "#fca5a5" }} data-testid="fx-risk-rr">{rr.toFixed(2)} : 1</strong>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>DAILY LOSS CAP</span>
          <strong style={{ ...S.cellVal, color: dailyCapHit ? "#ef4444" : "#10b981" }} data-testid="fx-daily-cap-usd">
            {fmtUSD(dailyLossCapUsd)} · {fmtPct(state.dailyLossCapPct)}
          </strong>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>P/L TODAY (PAPER)</span>
          <strong
            style={{ ...S.cellVal, color: state.paperPnlToday >= 0 ? "#10b981" : "#ef4444" }}
            data-testid="fx-pnl-today"
          >{fmtUSD(state.paperPnlToday || 0)}</strong>
        </div>
        <div style={S.cell}>
          <span style={S.cellLbl}>REMAINING CAP</span>
          <strong style={{ ...S.cellVal, color: dailyRemaining > 0 ? "#fcd34d" : "#ef4444" }} data-testid="fx-cap-remaining">
            {fmtUSD(dailyRemaining)}
          </strong>
        </div>
      </div>

      {dailyCapHit && (
        <div style={S.capAlert} role="alert" data-testid="fx-daily-cap-hit">
          ⚠ Daily loss cap reached. New paper positions are blocked until tomorrow (UTC).
          This is a discipline guardrail — use the cool-off period before any real trading.
        </div>
      )}

      <div style={S.actions}>
        <button
          type="button"
          onClick={addPaperPosition}
          disabled={dailyCapHit || !state.paperMode || !units}
          style={{
            ...S.primary,
            opacity: dailyCapHit || !state.paperMode || !units ? 0.45 : 1,
            cursor: dailyCapHit || !state.paperMode || !units ? "not-allowed" : "pointer",
          }}
          data-testid="fx-paper-stage"
          title={!state.paperMode ? "Enable Paper Mode to stage virtual positions" : ""}
        >
          + Stage Paper {side.toUpperCase()} · {units.toLocaleString()} units
        </button>
        <button type="button" onClick={resetDailyPnl} style={S.ghost} data-testid="fx-reset-pnl">
          Reset P/L (Today)
        </button>
      </div>

      <div style={S.tableWrap} data-testid="fx-position-manager">
        <div style={S.tableHead}>
          <strong style={{ color: "#c5a059" }}>Position Manager · Paper Validation Log</strong>
          <span style={S.tableCount}>{(state.paperPositions || []).length} positions</span>
        </div>
        {(state.paperPositions || []).length === 0 ? (
          <p style={S.empty}>No paper positions staged. Use the Stage Paper button above.</p>
        ) : (
          <div style={S.tableBody}>
            {(state.paperPositions || []).map((p) => (
              <div key={p.id} style={S.row} data-testid={`fx-position-${p.id}`}>
                <span style={{ ...S.pill, color: p.side === "buy" ? "#10b981" : "#ef4444", borderColor: "currentColor" }}>
                  {p.side.toUpperCase()}
                </span>
                <code style={S.codeChip}>{p.instrument}</code>
                <span style={S.colSmall}>{p.units.toLocaleString()} u</span>
                <span style={S.colSmall}>@ {Number(p.entry).toFixed(decimals)}</span>
                <span style={{ ...S.pill, color: p.status === "OPEN" ? "#fcd34d" : p.status === "TP_HIT" ? "#10b981" : p.status === "SL_HIT" ? "#ef4444" : "#94a3b8", borderColor: "currentColor" }}>
                  {p.status}
                </span>
                <span style={{ ...S.colSmall, color: p.pnl_usd >= 0 ? "#10b981" : "#ef4444" }}>
                  {p.pnl_usd ? fmtUSD(p.pnl_usd) : "—"}
                </span>
                {p.status === "OPEN" && (
                  <div style={S.actionsRow}>
                    <button type="button" onClick={() => closePaperPosition(p.id, "TARGET")} style={S.tinyOk} data-testid={`fx-position-${p.id}-tp`}>TP</button>
                    <button type="button" onClick={() => closePaperPosition(p.id, "STOP")} style={S.tinyBad} data-testid={`fx-position-${p.id}-sl`}>SL</button>
                    <button type="button" onClick={() => closePaperPosition(p.id, "MANUAL")} style={S.tinyGhost} data-testid={`fx-position-${p.id}-close`}>Close</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <p style={S.note}>
        Risk Calculator is a research helper. Pip-value calculations assume USD-quoted pairs; for
        cross pairs, re-check at your broker. <strong>No autonomous execution.</strong> Use Paper
        Mode to validate setups before risking real capital. The daily loss cap is a discipline
        guardrail, not a hard broker rule.
      </p>
    </section>
  );
}

const S = {
  wrap: {
    marginTop: 12,
    padding: 14,
    background: "rgba(11,15,20,0.94)",
    border: "1px solid rgba(197,160,89,0.18)",
    borderRadius: 14,
    color: "#e2e8f0",
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  title: { color: "#fde68a", fontSize: 14, letterSpacing: "0.04em" },
  headRight: { display: "flex", alignItems: "center", gap: 8 },
  modePill: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 10.5,
    letterSpacing: "0.10em",
    fontWeight: 800,
    padding: "4px 10px",
    border: "1px solid",
    borderRadius: 999,
  },
  btnSm: {
    appearance: "none",
    background: "transparent",
    border: "1px solid rgba(197,160,89,0.45)",
    color: "#c5a059",
    padding: "4px 10px",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
    letterSpacing: "0.06em",
    fontWeight: 700,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
    marginTop: 4,
  },
  lbl: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: "#94a3b8",
    fontFamily: "'Space Mono', monospace",
    fontSize: 10.5,
    letterSpacing: "0.06em",
  },
  input: {
    appearance: "none",
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.06)",
    color: "#e2e8f0",
    padding: "8px 10px",
    borderRadius: 8,
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
    fontSize: 13,
  },
  sideRow: { display: "flex", gap: 6 },
  sideBtn: {
    flex: 1,
    appearance: "none",
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "#94a3b8",
    padding: "8px 0",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11.5,
    fontWeight: 800,
    letterSpacing: "0.08em",
  },
  sideBtnActiveBuy: {
    background: "rgba(16,185,129,0.12)",
    borderColor: "#10b981",
    color: "#10b981",
  },
  sideBtnActiveSell: {
    background: "rgba(239,68,68,0.12)",
    borderColor: "#ef4444",
    color: "#ef4444",
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 8,
    marginTop: 12,
  },
  cell: {
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.05)",
    borderRadius: 10,
    padding: "8px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  cellLbl: {
    color: "#64748b",
    fontFamily: "'Space Mono', monospace",
    fontSize: 9.5,
    letterSpacing: "0.10em",
  },
  cellVal: { fontSize: 16, fontWeight: 800 },
  capAlert: {
    marginTop: 10,
    padding: "8px 10px",
    background: "rgba(239,68,68,0.12)",
    border: "1px solid rgba(239,68,68,0.40)",
    color: "#fecaca",
    borderRadius: 10,
    fontSize: 12,
  },
  actions: { display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" },
  primary: {
    appearance: "none",
    background: "linear-gradient(135deg, #c5a059, #ffd966)",
    border: "1px solid #c5a059",
    color: "#0c1a10",
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11.5,
    fontWeight: 800,
    letterSpacing: "0.08em",
  },
  ghost: {
    appearance: "none",
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#94a3b8",
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
    letterSpacing: "0.05em",
  },
  tableWrap: { marginTop: 12 },
  tableHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
  tableCount: { fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#64748b" },
  tableBody: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: 220,
    overflowY: "auto",
    paddingRight: 4,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "auto auto 70px auto auto auto 1fr",
    gap: 8,
    alignItems: "center",
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.05)",
    padding: "6px 10px",
    borderRadius: 8,
  },
  pill: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 9.5,
    letterSpacing: "0.10em",
    fontWeight: 800,
    padding: "2px 8px",
    border: "1px solid",
    borderRadius: 999,
  },
  codeChip: {
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    padding: "2px 8px",
    borderRadius: 6,
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
  },
  colSmall: { fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#cbd5e1" },
  actionsRow: { display: "flex", gap: 4, justifyContent: "flex-end" },
  tinyOk: {
    appearance: "none",
    background: "rgba(16,185,129,0.12)",
    border: "1px solid #10b981",
    color: "#10b981",
    padding: "2px 8px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 10,
    fontFamily: "'Space Mono', monospace",
    fontWeight: 800,
  },
  tinyBad: {
    appearance: "none",
    background: "rgba(239,68,68,0.12)",
    border: "1px solid #ef4444",
    color: "#ef4444",
    padding: "2px 8px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 10,
    fontFamily: "'Space Mono', monospace",
    fontWeight: 800,
  },
  tinyGhost: {
    appearance: "none",
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#94a3b8",
    padding: "2px 8px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 10,
    fontFamily: "'Space Mono', monospace",
    fontWeight: 800,
  },
  empty: {
    margin: 0,
    padding: "10px 0",
    color: "#64748b",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11.5,
    textAlign: "center",
  },
  note: {
    marginTop: 10,
    color: "#94a3b8",
    fontSize: 11,
    fontStyle: "italic",
    lineHeight: 1.4,
  },
};
